import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Input,
  InputNumber,
  DatePicker,
  Dropdown,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import { staticConfig, apiConfig } from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const STATUS_COLORS = {
  Pending: { bg: "#FFFAEB", color: "#B54708" },
  Active: { bg: "#ECFDF3", color: "#027A48" },
  Closed: { bg: "#F2F4F7", color: "#344054" },
  Matured: { bg: "#EFF8FF", color: "#175CD3" },
  Rejected: { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
    </Tooltip>
  </span>
);

const SectionHeader = ({ title }) => (
  <div style={{ marginBottom: 16, marginTop: 8 }}>
    <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#344054" }}>
      {title}
    </p>
    <div style={{ borderBottom: "1px solid #EAECF0", marginTop: 6 }} />
  </div>
);

const parseDate = (v) => {
  if (!v) return null;
  if (Array.isArray(v)) {
    const [y, m, d] = v;
    return dayjs(
      `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    );
  }
  return dayjs(v);
};

const ViewTreasuryBillContract = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [statusText, setStatusText] = useState(null);
  const [statusCode, setStatusCode] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const contractId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    if (contractId) fetchContractDetail(contractId);
  }, [contractId]);

  const fetchContractDetail = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTreasuryBillContract}/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load contract.",
        );
        setIsLoading(false);
        return;
      }

      setStatusText(res.status?.text ?? res.status ?? null);

      setStatusCode(res.status?.code ?? res.status ?? null);

      form.setFieldsValue({
        contractReferenceId: res.contractReferenceId ?? "—",
        clientName: res.client?.displayName ?? "—",
        productName: res.product?.productName ?? "—",
        linkedAccount: res.linkedAccount?.accountNo ?? "—",
        custodianName: res.custodian?.name ?? "—",
        currency: res.currency?.code ?? "—",
        faceValue: res.faceValue ?? null,
        purchasePrice: res.purchasePrice ?? null,
        discountRate: res.discountRate ?? null,
        maturityValue: res.maturityValue ?? null,
        netMaturityValue: res.netMaturityValue ?? null,
        yieldPercentage: res.yieldPercentage ?? null,
        tenor: res.tenor ?? null,
        totalInterestAccrued: res.totalInterestAccrued ?? null,
        totalInterestPosted: res.totalInterestPosted ?? null,
        totalFees: res.totalFees ?? null,
        interestType: res.interestType?.text ?? "—",
        amlScreening: res.amlScreening?.text ?? "—",
        statusDescription: res.status?.text ?? "—",
        submittedOnDate: parseDate(res.submittedOnDate),
        issueDate: parseDate(res.issueDate),
        settlementDate: parseDate(res.settlementDate),
        valueDate: parseDate(res.valueDate),
        kycValidated: res.kycValidated ? "Yes" : "No",
        withholdTax: res.withholdTax ? "Yes" : "No",
        allowRollover: res.allowRollover ? "Yes" : "No",
        autoRollover: res.autoRollover ? "Yes" : "No",
        autoMaturityTransfer: res.autoMaturityTransfer ? "Yes" : "No",
        centralBankAuctionRefLinked: res.centralBankAuctionRefLinked
          ? "Yes"
          : "No",
      });

      setIsLoading(false);
    });
  };

  const runAction = (suffix, successMsg) => {
    setIsLoading(true);
    const body = { session, method: "POST" };
    const url = `${apiConfig.GetTreasuryBillContract}/${contractId}/${suffix}`;

    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Action failed.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, successMsg);
      setIsLoading(false);
      fetchContractDetail(contractId);
    });
  };

  const handleAction = ({ key }) => {
    switch (key) {
      case "approve":
        navigate("/treasury-bill-contract/approve", { state: contractId });
        break;
      case "reject":
        navigate("/treasury-bill-contract/reject", { state: contractId });
        break;
      case "disburse":
        navigate("/treasury-bill-contract/disburse", { state: contractId });
        break;
      case "premature_redemption":
        navigate("/treasury-bill-contract/premature-redemption", {
          state: contractId,
        });
        break;
      case "rollover":
        navigate("/treasury-bill-contract/rollover", { state: contractId });
        break;
      case "close":
        navigate("/treasury-bill-contract/close", { state: contractId });
        break;
      default:
        break;
    }
  };

  const actionMenuItems = [
    {
      key: "approve",
      label: "Approve Contract",
      // Enable only when submitted (pending approval)
      disabled: statusCode !== "contractStatus.submitted",
    },
    {
      key: "reject",
      label: "Reject Contract",
      // Enable when submitted or approved
      disabled:!["contractStatus.submitted", "contractStatus.approved"].includes(statusCode),
    },
    { type: "divider" },
    {
      key: "disburse",
      label: "Disburse Contract",
      // Enable only when approved
      disabled: statusCode !== "contractStatus.approved",
    },
    {
      key: "premature_redemption",
      label: "Premature Redemption",
      disabled: statusCode !== "contractStatus.active",
    },
    {
      key: "rollover",
      label: "Rollover Contract",
      disabled: statusCode !== "contractStatus.active",
    },
    { type: "divider" },
    {
      key: "close",
      label: (
        <span
          style={{
            color: [
              "contractStatus.closed",
              "contractStatus.rejected",
            ].includes(statusCode)
              ? "#98A2B3"
              : "#B42318",
          }}
        >
          Close Contract
        </span>
      ),
      disabled: ["contractStatus.closed", "contractStatus.rejected"].includes(
        statusCode,
      ),
    },
  ];

  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };
  const fmt = (v) => (v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "");

  return (
    <MainLayout pageItems={staticConfig.newtreasuryBillContract}>
      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            {/* Header */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0, fontWeight: 600 }}>
                  Treasury Bill Contract Details
                </h3>
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>
              {/* ── Section 1: Contract Info ───────────────────────────── */}
              {/* <SectionHeader title="Contract Information" /> */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Contract Reference"
                        tip="Unique system-generated reference."
                      />
                    }
                    name="contractReferenceId"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Status"
                        tip="Current status of this contract."
                      />
                    }
                    name="statusDescription"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Client"
                        tip="The client this contract belongs to."
                      />
                    }
                    name="clientName"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip text="Security Instrument" tip="The treasury bill product." />
                    }
                    name="productName"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Linked Account"
                        tip="The savings account linked to this contract."
                      />
                    }
                    name="linkedAccount"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Custodian"
                        tip="The custodian holding the treasury bill."
                      />
                    }
                    name="custodianName"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Currency"
                        tip="The currency of this contract."
                      />
                    }
                    name="currency"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Interest Type"
                        tip="Fixed or variable interest."
                      />
                    }
                    name="interestType"
                  >
                    <Input
                      className="of-bold-disable-input"
                      style={ds}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Section 2: Dates ──────────────────────────────────── */}
              <SectionHeader title="Key Dates" />
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Submitted On"
                        tip="Date this contract was submitted."
                      />
                    }
                    name="submittedOnDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Issue Date"
                        tip="The date the treasury bill was issued."
                      />
                    }
                    name="issueDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Date"
                        tip="The settlement / maturity date."
                      />
                    }
                    name="settlementDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Value Date"
                        tip="The value date for this contract."
                      />
                    }
                    name="valueDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Tenor (Days)"
                        tip="Days from issue to maturity."
                      />
                    }
                    name="tenor"
                  >
                    <InputNumber
                      className="of-bold-disable-input"
                      style={{ width: "100%", ...ds }}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Section 3: Financials ─────────────────────────────── */}
              <SectionHeader title="Financial Details" />
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip text="Face Value" tip="The nominal / par value." />
                    }
                    name="faceValue"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={fmt}
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Purchase Price"
                        tip="The discounted price paid."
                      />
                    }
                    name="purchasePrice"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={fmt}
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Discount Rate (%)"
                        tip="The annualised discount rate."
                      />
                    }
                    name="discountRate"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      precision={2}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Yield (%)"
                        tip="The calculated yield percentage."
                      />
                    }
                    name="yieldPercentage"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      precision={4}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip text="Maturity Value" tip="The value at maturity." />
                    }
                    name="maturityValue"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={fmt}
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Net Maturity Value"
                        tip="Maturity value after fees and tax."
                      />
                    }
                    name="netMaturityValue"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={fmt}
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Interest Accrued"
                        tip="Cumulative interest accrued."
                      />
                    }
                    name="totalInterestAccrued"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={fmt}
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      className="of-bold-disable-input"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Interest Posted"
                        tip="Interest posted to the linked account."
                      />
                    }
                    name="totalInterestPosted"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={fmt}
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      className="of-bold-disable-input"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Total Fees"
                        tip="Total fees charged on this contract."
                      />
                    }
                    name="totalFees"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={fmt}
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="AML Screening"
                        tip="Anti-money laundering screening result."
                      />
                    }
                    name="amlScreening"
                  >
                    <Input
                      style={ds}
                      disabled
                      className="of-bold-disable-input"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Section 4: Flags ──────────────────────────────────── */}
              <SectionHeader title="Contract Flags" />
              <Row gutter={[24, 0]}>
                {[
                  { name: "kycValidated", label: "KYC Validated" },
                  { name: "withholdTax", label: "Withhold Tax" },
                  { name: "allowRollover", label: "Allow Rollover" },
                  { name: "autoRollover", label: "Auto Rollover" },
                  {
                    name: "autoMaturityTransfer",
                    label: "Auto Maturity Transfer",
                  },
                  {
                    name: "centralBankAuctionRefLinked",
                    label: "Central Bank Auction Ref Linked",
                  },
                ].map(({ name, label }) => (
                  <Col span={8} key={name} style={{ padding: "0 8px" }}>
                    <Form.Item label={label} name={name}>
                      <Input
                        style={ds}
                        disabled
                        className="of-bold-disable-input"
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(
  ViewTreasuryBillContract,
);
